
import { SocialMediaProfile, SocialMediaMetrics, SocialMediaContent, SocialMediaConnection } from '@/types/socialMedia';

// Function to fetch profiles from each connected platform
export const getSocialMediaProfiles = async (userId: string): Promise<SocialMediaProfile[]> => {
  try {
    // In a production environment, we would fetch real data from the database
    // or directly from social media APIs using OAuth tokens
    // For now, return mock data
    if (process.env.NODE_ENV === 'development' || !userId) {
      return generateMockSocialMediaProfiles();
    }
    
    // This would be replaced with actual fetch from database/APIs in production
    return [];
  } catch (error) {
    console.error('Failed to fetch social profiles:', error);
    
    // Return mock data in development or if there's an error
    if (process.env.NODE_ENV === 'development') {
      return generateMockSocialMediaProfiles();
    }
    
    return [];
  }
};

// Function to get recent content from social platforms
export const getSocialMediaContent = async (
  userId: string,
  platform?: 'instagram' | 'twitter' | 'facebook' | 'youtube',
  limit = 10
): Promise<SocialMediaContent[]> => {
  try {
    // In a production environment, we would fetch from database or APIs
    // For now, use mock data
    if (process.env.NODE_ENV === 'development' || !userId) {
      return generateMockSocialMediaContent(platform);
    }
    
    // This would be replaced with actual fetch from database/APIs in production
    return [];
  } catch (error) {
    console.error('Failed to fetch social content:', error);
    // Return mock data in development
    if (process.env.NODE_ENV === 'development') {
      return generateMockSocialMediaContent(platform);
    }
    return [];
  }
};

// Function to get user's social media connections
export const getUserSocialConnections = async (userId: string): Promise<SocialMediaConnection[]> => {
  try {
    // In a production environment, we would fetch from database
    // For now, use mock data
    if (process.env.NODE_ENV === 'development' || !userId) {
      return generateMockSocialConnections();
    }
    
    // This would be replaced with actual fetch from database in production
    return [];
  } catch (error) {
    console.error('Failed to fetch social connections:', error);
    if (process.env.NODE_ENV === 'development') {
      return generateMockSocialConnections();
    }
    return [];
  }
};

// Mock data generators
const generateMockSocialConnections = (): SocialMediaConnection[] => {
  return [
    {
      platform: 'instagram',
      status: 'connected',
      lastConnected: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
      scopes: ['user_profile', 'user_media']
    },
    {
      platform: 'twitter',
      status: 'connected',
      lastConnected: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
      scopes: ['user_profile', 'tweets']
    },
    {
      platform: 'facebook',
      status: 'disconnected',
      scopes: []
    },
    {
      platform: 'youtube',
      status: 'disconnected',
      scopes: []
    }
  ];
};

const generateMockProfileForPlatform = (platform: 'instagram' | 'twitter' | 'facebook' | 'youtube'): SocialMediaProfile => {
  const baseFollowers = {
    instagram: 4218,
    twitter: 2842,
    facebook: 1968,
    youtube: 3512
  };
  
  return {
    platform,
    profileId: `mock-${platform}-id`,
    username: `artist_${platform}`,
    displayName: platform.charAt(0).toUpperCase() + platform.slice(1) + ' Profile',
    followerCount: baseFollowers[platform],
    followingCount: Math.floor(baseFollowers[platform] * 0.3),
    postCount: Math.floor(Math.random() * 100) + 50,
    lastUpdated: new Date(),
    metrics: {
      engagement: {
        likes: Math.floor(Math.random() * 1000) + 500,
        comments: Math.floor(Math.random() * 200) + 50,
        shares: Math.floor(Math.random() * 100) + 10,
        averageEngagementRate: Math.random() * 5 + 1
      },
      growth: {
        followerGrowth: Math.random() * 20 - 5, // Between -5% and 15%
        period: 'month',
        previousCount: Math.floor(baseFollowers[platform] * (1 - (Math.random() * 0.2 - 0.05)))
      },
      performance: {
        bestPerformingContent: []
      }
    },
    isConnected: true
  };
};

const generateMockSocialMediaProfiles = (): SocialMediaProfile[] => {
  return [
    generateMockProfileForPlatform('instagram'),
    generateMockProfileForPlatform('twitter'),
    generateMockProfileForPlatform('facebook'),
    generateMockProfileForPlatform('youtube')
  ];
};

const generateMockSocialMediaContent = (platform?: 'instagram' | 'twitter' | 'facebook' | 'youtube'): SocialMediaContent[] => {
  const platforms = platform ? [platform] : ['instagram', 'twitter', 'facebook', 'youtube'];
  const contentTypes: Record<string, string[]> = {
    instagram: ['post', 'story', 'reel'],
    twitter: ['tweet'],
    facebook: ['post', 'video'],
    youtube: ['video']
  };
  
  const result: SocialMediaContent[] = [];
  
  platforms.forEach(plt => {
    const types = contentTypes[plt];
    for (let i = 0; i < 5; i++) {
      const contentType = types[Math.floor(Math.random() * types.length)] as 'post' | 'story' | 'reel' | 'tweet' | 'video';
      const publishDate = new Date();
      publishDate.setDate(publishDate.getDate() - Math.floor(Math.random() * 30));
      
      const likes = Math.floor(Math.random() * 500) + 20;
      const views = contentType === 'video' || contentType === 'reel' ? Math.floor(likes * (Math.random() * 5 + 3)) : undefined;
      
      result.push({
        id: `mock-${plt}-${i}`,
        platform: plt as any,
        contentType,
        url: `https://example.com/${plt}/${i}`,
        caption: `This is a sample ${contentType} on ${plt}`,
        publishDate,
        metrics: {
          views,
          likes,
          comments: Math.floor(likes * (Math.random() * 0.3 + 0.05)),
          shares: Math.floor(likes * (Math.random() * 0.2 + 0.02)),
          engagement: Math.random() * 8 + 1,
          comparedToAverage: (Math.random() * 300) - 50 // Between -50% and 250%
        },
        thumbnailUrl: `https://picsum.photos/seed/${plt}${i}/300/300`
      });
    }
  });
  
  return result.sort((a, b) => b.publishDate.getTime() - a.publishDate.getTime());
};
